import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PasswordInput } from "@/shared/ui/password-input";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { useAuth } from "@/features/auth/model/AuthContext";
import { useLogin } from "@/features/auth/hooks/auth.hooks";
import { VersionInfo } from "@/shared/widgets/VersionInfo";
import { AuthLayout } from "./AuthLayout";
import { AuthCard, AuthCardHeader, AuthCardContent } from "./AuthCard";
import { LogoHeader, AuthHeader } from "./AuthHeader";
import { useTranslation } from "@/shared/i18n";

export default function Auth() {
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [isLogin] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { login: setAuthUser } = useAuth();

  const loginMutation = useLogin({
    onSuccess: (data) => {
      if (data && data.userId) {
        setAuthUser(data);
        if (data.role === "ADMIN") {
          navigate("/");
        } else {
          navigate("/stats");
        }
      }
    },
    onError: (error) => {
      setAuthError(error.message || t('authError'));
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpa o erro do campo ao começar a digitar
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.email) {
      newErrors.email = t('emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('passwordMinLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setAuthError(null);

    try {
      await loginMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
      });
    } catch (error) {
      // Erro já tratado no onError do mutation
      console.error("Login error:", error);
    }
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthCardHeader>
          <LogoHeader />
          <AuthHeader
            title={isLogin ? t('welcomeBack') : t('createAccount')}
            description={isLogin
              ? t('enterWithEmail')
              : t('fillDataToCreate')}
          />
        </AuthCardHeader>
        <AuthCardContent>
          {authError && (
            <Alert
              variant="destructive"
              className="mb-4 bg-red-900/20 border-red-900 text-white"
            >
              <AlertDescription>{authError}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">{t('email')}</Label>
              <div className="relative">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('emailPlaceholder')}
                  className={`bg-[#2A2A2A] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#D91657] focus-visible:ring-offset-0 ${errors.email ? 'border-red-500' : ''
                    }`}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-white/80">{t('password')}</Label>
              </div>
              <div className="relative">
                <PasswordInput
                  id="password"
                  name="password"
                  placeholder="••••••••"
                  className={`bg-[#2A2A2A] border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#D91657] focus-visible:ring-offset-0 ${errors.password ? 'border-red-500' : ''
                    }`}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="link"
                  className="p-0 self-end h-auto text-xs text-white/60 hover:text-[#D91657] hover:no-underline"
                  onClick={() => navigate("/auth/forgot-password")}
                >
                  {t('forgotPassword')}
                </Button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? t('loading') : isLogin ? t('signIn') : t('createAccount')}
            </Button>
          </form>
        </AuthCardContent>
      </AuthCard>
      <div className="fixed bottom-4 right-4">
        <VersionInfo />
      </div>
    </AuthLayout>
  );
}